/**
 * Inline attachment display for sent messages.
 * Shows small image thumbnails and file tags inside chat bubbles.
 */

import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";

import "./MessageAttachments.scss";

export interface AttachmentFileMeta {
  filename: string;
  mimeType: string;
}

interface MessageAttachmentsProps {
  imageUrls?: string[];
  files?: AttachmentFileMeta[];
}

export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  imageUrls,
  files,
}) => {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const closeLightbox = useCallback(() => setLightboxUrl(null), []);

  const hasImages = imageUrls && imageUrls.length > 0;
  const hasFiles = files && files.length > 0;

  if (!hasImages && !hasFiles) {
    return null;
  }

  return (
    <>
      <div className="msg-attach">
        {hasImages && (
          <div className="msg-attach__images">
            {imageUrls.map((url, i) => (
              <button
                key={i}
                type="button"
                className="msg-attach__thumb-btn"
                onClick={() => setLightboxUrl(url)}
                aria-label={`Image ${i + 1}`}
              >
                <img
                  src={url}
                  alt=""
                  className="msg-attach__thumb"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
        {hasFiles && (
          <div className="msg-attach__files">
            {files.map((f, i) => (
              <span key={i} className="msg-attach__file-tag">
                <span className="msg-attach__file-icon material-symbols-outlined">
                  description
                </span>
                <span className="msg-attach__file-name">{f.filename}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {lightboxUrl &&
        createPortal(
          <div
            className="msg-attach-lightbox"
            onClick={closeLightbox}
            role="dialog"
            aria-modal="true"
          >
            <img
              src={lightboxUrl}
              alt=""
              className="msg-attach-lightbox__img"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              className="msg-attach-lightbox__close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};
